import { checkinDeadline, checkinOpensAt, effectiveDue, isAssigneeOf, isClosedFor, isTargetOf } from './derive';
import { formatClock, formatDay, formatDue } from './time';

/** Inbox (W8) — 알림 · 태스크 · 체크포인트 · 댓글 상태로 Needs action · Activity 줄을 만든다 */

export const DUE_SOON_MS = 2 * 3_600_000;
const ACCEPT_MS = 3_600_000;
const URGENT_MS = 30 * 60_000;
/** 체크포인트 미리 알림 — 다음 체크포인트 15분 전부터 (지난 체크포인트를 한꺼번에 띄우지 않는다) */
const CHECKIN_REMIND_MS = 15 * 60_000;
const HOUR_MS = 3_600_000;
const MIN = 60_000;
const NPC_NAME = 'Team lead Kim';

const ms = (iso) => new Date(iso).getTime();

const durLabel = (diff) => {
  const m = Math.max(1, Math.ceil(diff / MIN));
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}`;
};

export const agoLabel = (at, now, tz) => {
  const m = Math.floor((now.getTime() - at) / MIN);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m} min ago`;
  if (formatDay(new Date(at), tz) !== formatDay(now, tz)) return formatDue(new Date(at), tz);
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return h < 3 && rest >= 5 ? `${h}h ${rest}m ago` : `${h}h ago`;
};

const lowerFirst = (s) => s.charAt(0).toLowerCase() + s.slice(1);

const versionsOf = (task) => [
  ...(task.history ?? []).map((h) => ({ d: h.deliverable, r: h.review })),
  ...(task.deliverable ? [{ d: task.deliverable, r: task.review }] : []),
];

const reviewLabel = (r) =>
  r?.returned ? 'Sent back' : r?.result === 'approved' ? 'Approved' : 'Changes requested';

const goOf = (n, focus) =>
  n.link?.to === 'task' && n.link.id
    ? { to: 'task', taskId: n.link.id, focus, commentId: n.link.commentId ?? null }
    : { to: n.link?.to ?? 'tasks' };

// 바로 알려야 하는 활동 — 멘션 · 답글 · 예상 제출 시각 변경 · 운영진 답장
export const isToastable = (item) =>
  item.tab === 'action'
    ? !item.done
    : item.unread && ['mention', 'reply', 'expected', 'organizer', 'overdue', 'announcement'].includes(item.type);

/**
 * 처음 들어온 사람에게 띄우는 사용법 안내 — 서버 알림이 아니라 화면이 만든다. 읽으면 브라우저에 기억 (guideRead)
 * 순서: 환영 → 할 일 수락 → 체크인
 */
const guidesFor = (data, viewer) => {
  const first = (viewer.name ?? '').split(' ')[0];
  return [
    {
      key: 'welcome',
      text: `Welcome to ${data.project.title}${first ? `, ${first}` : ''}! 👋`,
      snippet:
        'For the next 2 days you’ll ship a real product with your team, just like at work. Start with the two steps below.',
      go: { to: 'mine' },
    },
    {
      key: 'tasks',
      text: 'Check My tasks and accept yours',
      snippet:
        'Accept or decline each task within 1 hour of being assigned, and set when you expect to submit. Your teammates wait on your handoff.',
      go: { to: 'mine' },
    },
    {
      key: 'checkin',
      text: 'Check in on time at every checkpoint',
      snippet:
        'Checkpoints shows when the next check-in is. On-time check-ins count toward your evaluation, and missed ones are recorded.',
      go: { to: 'checkpoints' },
    },
  ];
};

export const buildInbox = ({ data, now, memberById, taskById, tz, guideRead = new Set() }) => {
  const me = data.viewerId;
  const viewer = memberById.get(me);
  const t = now.getTime();
  const closed = isClosedFor(data, now);
  const comments = data.comments ?? [];
  const commentById = new Map(comments.map((c) => [c.id, c]));
  const notes = (data.notifications ?? []).filter(
    (n) => n.userId === me && !n.kind?.startsWith('checkin_missed:'),
  );
  const nameOf = (id) => (id === 'npc' ? NPC_NAME : (memberById.get(id)?.name ?? null));
  const clock = (at) => formatClock(new Date(at), tz);
  const ago = (at) => agoLabel(at, now, tz);
  const repliedTo = (q, after) =>
    comments.some((c) => c.parentId === q.id && c.authorId === me && ms(c.createdAt) >= after);
  const submittedAfter = (task, after) =>
    versionsOf(task).some((v) => v.d?.submittedAt && ms(v.d.submittedAt) > after);
  const laterNote = (kind, taskId, at, inclusive = false) =>
    notes.some(
      (x) =>
        x.kind === kind &&
        x.link?.id === taskId &&
        (inclusive ? ms(x.createdAt) >= at : ms(x.createdAt) > at),
    );

  const actions = [];
  const activity = [];
  const reactions = new Map();
  const blank = { done: false, doneLabel: '', unread: false, noteIds: [] };

  const assignStatus = (task, at) => {
    if (!task) return 'Deleted';
    if (task.state === 'cancelled') return 'Cancelled';
    if (!isAssigneeOf(task, me)) return laterNote('task_reassigned', task.id, at, true) ? 'Reassigned' : 'Declined';
    if (laterNote('task_assigned', task.id, at)) return 'Reassigned';
    if (task.state === 'assigned') return null;
    return task.state === 'declined' ? 'Declined' : 'Accepted';
  };

  const reviewStatus = (task, at) => {
    if (!task) return 'Deleted';
    const versions = versionsOf(task);
    const idx = versions.reduce(
      (found, v, i) => (v.d?.submittedAt && ms(v.d.submittedAt) <= at + 5000 ? i : found),
      -1,
    );
    if (idx >= 0 && idx < versions.length - 1) return reviewLabel(versions[idx].r);
    if (task.state === 'submitted') return task.receiverId === me ? null : 'Receiver changed';
    if (task.state === 'approved') return 'Approved';
    if (task.state === 'rework_requested') return reviewLabel(task.review);
    return task.state === 'cancelled' ? 'Cancelled' : 'Receiver changed';
  };

  const reworkStatus = (task, at) => {
    if (!task) return 'Deleted';
    if (submittedAfter(task, at)) return 'Resubmitted';
    if (task.state === 'cancelled') return 'Cancelled';
    if (!isAssigneeOf(task, me)) return 'Reassigned';
    return task.state === 'rework_requested' ? null : 'Resubmitted';
  };

  const questionStatus = (q, task, after) => {
    if (!q) return 'Deleted';
    if (repliedTo(q, after)) return 'Replied';
    if (q.resolvedAt) return 'Resolved';
    if (q.type === 'blocker' && task && !task.isBlocked) return 'Resolved';
    return null;
  };

  notes.forEach((n) => {
    const at = ms(n.createdAt);
    const task = n.link?.to === 'task' && n.link.id ? taskById.get(n.link.id) : null;
    const title = task?.title;
    const who = nameOf(n.actorId);
    const say = (text) => (who && title ? text : n.text);
    const c = n.link?.commentId ? commentById.get(n.link.commentId) : null;
    const base = {
      ...blank,
      id: `n:${n.id}`,
      actorId: n.actorId ?? null,
      at,
      unread: !n.readAt,
      noteIds: [n.id],
      taskId: task?.id ?? null,
    };
    const action = (o, status) =>
      actions.push({ ...base, tab: 'action', ...o, done: status != null, doneLabel: status ?? '' });
    const act = (o) => activity.push({ ...base, tab: 'activity', type: 'other', go: goOf(n), ...o });

    switch (n.kind) {
      case 'task_assigned': {
        const status = assignStatus(task, at);
        const deadline = task?.assignedAt ? ms(task.assignedAt) + ACCEPT_MS : at + ACCEPT_MS;
        action(
          {
            type: 'assigned',
            text: n.text.includes(' assigned you ') ? say(`${who} assigned you “${title}”`) : n.text,
            // 수락 기한(1시간)이 지나도 수락할 수 있다 — 기한 대신 Delayed 만 남긴다
            meta: t > deadline ? 'Delayed' : `Accept by ${clock(deadline)}`,
            delayed: t > deadline,
            urgent: t <= deadline && deadline - t <= URGENT_MS,
            // 누르면 태스크 상세로 (수락 창을 바로 띄우지 않고 내용을 먼저 보게)
            go: goOf(n, 'actions'),
          },
          status,
        );
        break;
      }
      case 'handoff_submitted':
      case 'review_assigned':
        action(
          {
            type: 'review',
            text:
              n.kind === 'handoff_submitted' ? say(`${who} sent “${title}” for your review`) : n.text,
            meta: `Review requested · ${ago(at)}`,
            go: goOf(n, 'review'),
          },
          reviewStatus(task, at),
        );
        break;
      case 'handoff_rejected': {
        // 반려 사유 댓글 — 알림보다 늦지 않은 가장 최근 것. ✗ 항목 · 사유를 보여 주고, 누르면 그 댓글로 간다
        const reason = task
          ? comments
              .filter(
                (x) =>
                  x.taskId === task.id &&
                  x.type === 'rejection' &&
                  !x.parentId &&
                  ms(x.createdAt) <= at + 5000,
              )
              .sort((a, b) => ms(b.createdAt) - ms(a.createdAt))[0]
          : null;
        action(
          {
            type: 'changes',
            text: say(`${who} requested changes on “${title}”`),
            meta: `Submit a new version · ${ago(at)}`,
            ...(reason && {
              snippet: [reason.detail, reason.body].filter(Boolean).join('\n'),
            }),
            go: reason ? { ...goOf(n, 'comment'), commentId: reason.id } : goOf(n, 'actions'),
          },
          reworkStatus(task, at),
        );
        break;
      }
      case 'handoff_returned':
        action(
          {
            type: 'returned',
            text: say(`${who} sent back “${title}” with a question`),
            meta: `Reply to the question · ${ago(at)}`,
            ...(c && !closed && { btn: 'Reply', form: 'reply', snippet: c.body, replyTo: c.parentId ?? c.id }),
            go: goOf(n, c ? 'comment' : 'actions'),
          },
          !task ? 'Deleted' : c ? questionStatus(c, task, at) : reworkStatus(task, at),
        );
        break;
      case 'question_asked':
      case 'blocker_raised':
        action(
          {
            type: 'question',
            text: say(
              n.kind === 'blocker_raised'
                ? `${who} asked you for help in “${title}”`
                : `${who} asked you a question in “${title}”`,
            ),
            meta: `Needs your reply · ${ago(at)}`,
            ...(c && !closed && { btn: 'Reply', form: 'reply', snippet: c.body, replyTo: c.parentId ?? c.id }),
            go: goOf(n, 'comment'),
          },
          questionStatus(c, task, c ? ms(c.createdAt) : at),
        );
        break;
      case 'comment_mentioned':
      case 'comment_replied': {
        const mention = n.kind === 'comment_mentioned';
        act({
          type: mention ? 'mention' : 'reply',
          mention,
          text: say(
            mention
              ? `${who} mentioned you in “${title}”`
              : `${who} replied to your comment in “${title}”`,
          ),
          snippet: c?.body ?? '',
          replyTo: c ? (c.parentId ?? c.id) : null,
          reactTo: c?.id ?? null,
          go: goOf(n, 'comment'),
        });
        break;
      }
      case 'comment_reacted':
        if (n.link?.commentId) {
          reactions.set(n.link.commentId, [...(reactions.get(n.link.commentId) ?? []), n]);
        }
        break;
      case 'handoff_approved':
        act({ text: say(`${who} approved “${title}”`) });
        break;
      case 'scope_changed':
        act({ text: say(`${who} updated the checklist for “${title}”`) });
        break;
      case 'expected_changed': {
        // "… for “title”: Oct 16, 14:00 → Oct 16, 18:00 KST" — 바뀐 시각을 아래 줄로
        const change = n.text.includes('”: ') ? n.text.split('”: ').pop() : '';
        act({
          type: 'expected',
          text: say(`${who} changed the expected submit time for “${title}”`),
          snippet: change,
        });
        break;
      }
      case 'announcement':
      case 'change_event': {
        // 운영진 공지 — "Announcement · 제목: 본문" (300자까지). 누르면 지금 화면 위에 전문 모달
        // (change_event 는 예전 이름 — 같은 모달로)
        const m = /^(?:Announcement|Change event) · (.*?): ([\s\S]*)$/.exec(n.text);
        act({
          type: 'announcement',
          text: m ? `Announcement · ${m[1]}` : n.text,
          snippet: m ? m[2] : '',
          go: { to: 'notice', id: n.link?.to === 'notice' ? (n.link?.commentId ?? null) : null, text: n.text },
        });
        break;
      }
      case 'organizer_replied':
        // 누르면 운영진과의 메시지 창이 열린다
        act({
          type: 'organizer',
          text: 'The organizers replied to your message',
          snippet: n.text.replace(/^The organizers replied: /, ''),
          go: { to: 'organizer' },
        });
        break;
      default:
        // 마감 지난 태스크 — 담당자 · 리더에게 (서버가 한 번 보낸다)
        if (n.kind?.startsWith('task_overdue')) {
          act({ type: 'overdue', text: n.text, go: goOf(n, 'actions') });
          break;
        }
        act({ text: n.text });
    }
  });

  reactions.forEach((list, cid) => {
    const c = commentById.get(cid);
    if (!c) return;
    const people = [];
    Object.entries(c.reactions ?? {}).forEach(([emoji, ids]) =>
      (ids ?? []).forEach((id) => {
        if (id === me) return;
        const p = people.find((x) => x.id === id);
        if (p) p.emojis.push(emoji);
        else people.push({ id, emojis: [emoji] });
      }),
    );
    if (!people.length) return;
    const latest = list.reduce((a, b) => (ms(b.createdAt) > ms(a.createdAt) ? b : a));
    const lead = people.find((p) => p.id === latest.actorId) ?? people[0];
    const name = nameOf(lead.id) ?? 'Someone';
    const others = people.length - 1;
    activity.push({
      ...blank,
      tab: 'activity',
      id: `react:${cid}`,
      type: 'react',
      actorId: lead.id,
      at: ms(latest.createdAt),
      text: others
        ? `${name} and ${others} other${others > 1 ? 's' : ''} reacted to your comment`
        : `${name} reacted ${lead.emojis.join(' ')} to your comment`,
      unread: list.some((n) => !n.readAt),
      noteIds: list.map((n) => n.id),
      taskId: c.taskId,
      go: { to: 'task', taskId: c.taskId, focus: 'comment', commentId: cid },
    });
  });

  if (viewer) {
    (data.checkpoints ?? []).forEach((cp) => {
      if (!isTargetOf(cp, viewer)) return;
      const opens = checkinOpensAt(cp);
      if (t < opens) return;
      const deadline = checkinDeadline(cp, viewer);
      // 미리 알림은 다음 체크포인트 15분 전부터 마감까지만. 지난 체크포인트는 놓친 것만 (늦게라도 체크인할 수 있게)
      const remindFrom = Math.max(opens, ms(cp.scheduledAt) - CHECKIN_REMIND_MS);
      const ci = (data.checkins ?? []).find((x) => x.checkpointId === cp.id && x.userId === me);
      const name = cp.type === 'requested' ? cp.name : `“${cp.name}”`;
      const open = t <= deadline;
      const shared = {
        ...blank,
        tab: 'action',
        actorId: null,
        done: !!ci,
        doneLabel: ci ? 'Checked in' : '',
        form: 'checkin',
        checkpointId: cp.id,
        go: { to: 'checkpoints', checkpointId: cp.id },
      };
      // 프로젝트를 마쳤으면 체크인 할 일을 띄우지 않는다
      if (closed) return;
      if (open && t >= remindFrom) {
        actions.push({
          ...shared,
          id: `cp-open:${cp.id}`,
          type: 'checkpoint',
          at: remindFrom,
          text: `${name} is open · Check in by ${clock(deadline)}`,
          meta: `Due in ${durLabel(deadline - t)}`,
          urgent: deadline - t <= URGENT_MS,
          btn: 'Check in',
        });
      }
      if (!open && (!ci || ms(ci.checkedInAt) > deadline)) {
        actions.push({
          ...shared,
          id: `cp-miss:${cp.id}`,
          type: 'missed',
          at: deadline,
          text: `You missed ${cp.type === 'requested' ? lowerFirst(cp.name) : name}. You can still check in late.`,
          missed: true,
          meta: `Check in late · ${ago(deadline)}`,
          btn: 'Check in late',
        });
      }
    });
  }

  (data.tasks ?? []).forEach((task) => {
    if (!isAssigneeOf(task, me)) return;
    if (!['in_progress', 'rework_requested', 'submitted', 'approved'].includes(task.state)) return;
    const due = effectiveDue(task, taskById, now, data.project.endAt).getTime();
    const from = due - DUE_SOON_MS;
    if (t < from) return;
    const working = task.state === 'in_progress' || task.state === 'rework_requested';
    if (!working && !submittedAfter(task, from)) return;
    actions.push({
      ...blank,
      tab: 'action',
      id: `due:${task.id}`,
      type: 'due',
      actorId: null,
      at: from,
      // 마감이 지났으면 더 분명하게
      text:
        working && due <= t
          ? `“${task.title}” is overdue. Submit it as soon as you can`
          : `“${task.title}” is due soon`,
      ...(working && due <= t && {
        snippet: 'Tell your teammates where you are in a comment. A late submission lowers your score.',
      }),
      done: !working,
      doneLabel: working ? '' : 'Submitted',
      meta: due > t ? `Due in ${durLabel(due - t)}` : `Overdue · ${durLabel(t - due)}`,
      urgent: due - t <= URGENT_MS,
      taskId: task.id,
      go: { to: 'task', taskId: task.id, focus: 'actions' },
    });
  });

  // 사용법 안내 — 팀원은 팀 시작부터, PM 은 프로젝트 시작부터. 운영진(팀장) · 읽기 전용은 띄우지 않는다
  if (viewer && viewer.role !== 'npc' && !data.readOnly) {
    const startMs = ms(data.project.startAt);
    const teamMs = data.project.teamStartAt ? ms(data.project.teamStartAt) : startMs + HOUR_MS;
    const from = viewer.role === 'leader' ? startMs : teamMs;
    if (t >= from) {
      guidesFor(data, viewer).forEach((g, i) =>
        activity.push({
          ...blank,
          tab: 'activity',
          id: `guide:${g.key}`,
          type: 'guide',
          guide: g.key,
          actorId: null,
          at: from - i * 1000,
          text: g.text,
          snippet: g.snippet,
          unread: !guideRead.has(g.key),
          go: g.go,
        }),
      );
    }
  }

  const newest = (a, b) => b.at - a.at || (a.id < b.id ? 1 : -1);
  actions.sort(newest);
  activity.sort(newest);
  return {
    actions,
    activity,
    pending: actions.filter((i) => !i.done).length,
    unread: activity.filter((i) => i.unread).length,
  };
};
